// Liste der Opportunities (/opportunities/liste): Tabelle mit Filterpillen, Bedienleiste (Zähler, Sortierung, Spalten, Suche)
// und einer Summenzeile. Spalten und Sortierung merkt der Server je Nutzer (`opportunities`).
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { OpportunitySortierung, OpportunityZeile } from "../api/opportunities";
import { Laden, Leer } from "../components/Bausteine";
import { spalteVerschieben } from "../werkzeuge/spalten";
import { FehlerBox } from "../components/Fehler";
import { Bedienleiste } from "../components/smartview/Bedienleiste";
import { ListenSchluessel } from "../components/smartview/listenVertrag";
import { useMeta } from "../daten/meta";
import { useSeitenBefehle, useSeitenKopf } from "../daten/seite";
import { T } from "../texte";
import { ansichtLesen, ansichtMerken } from "./ansicht";
import { useListe } from "./daten";
import { FilterLeiste } from "./FilterLeiste";
import { OpportunityKopf } from "./Kopf";
import { apiAlsSort, euro, OPP_SPALTEN, sortAlsApi, spaltenWahl } from "./logik";
import { OpportunityDialog } from "./OpportunityDialog";
import { ALLE_SPALTEN, SORT_FELDER } from "./spalten";
import { useDarfAendern, useStatusWechsel } from "./statuswechsel";
import { OpportunityTabelle } from "./Tabelle";
import { useFilter } from "./zustand";
import "./opportunities.css";

const LISTE = ListenSchluessel.opportunities;
const STANDARD_SORT = "-date_updated";

interface Anfang {
  spalten: string[] | null;
  sort: string;
  breiten: Record<string, number>;
}

/** gemerkte Spaltenbreiten (px) aus den Einstellungen der Ansicht, nur gültige Zahlen */
function breitenAus(roh: unknown): Record<string, number> {
  const aus: Record<string, number> = {};
  if (roh && typeof roh === "object") for (const [k, v] of Object.entries(roh as Record<string, unknown>)) if (typeof v === "number" && v > 0) aus[k] = v;
  return aus;
}

export function ListeSeite() {
  const [anfang, setAnfang] = useState<Anfang | null>(null);
  useSeitenKopf([{ label: T.nav.opportunities }]);
  useEffect(() => {
    let weg = false;
    void ansichtLesen(LISTE).then((a) => {
      if (weg) return;
      const s = a.sortierung?.[0];
      setAnfang({ spalten: a.spalten, sort: s ? sortAlsApi(s, STANDARD_SORT) : STANDARD_SORT, breiten: breitenAus(a.einstellungen?.breiten) });
    });
    return () => {
      weg = true;
    };
  }, []);
  return anfang ? <ListeInhalt anfang={anfang} /> : <Laden />;
}

function ListeInhalt({ anfang }: { anfang: Anfang }) {
  const { meta } = useMeta();
  const navigate = useNavigate();
  const darfAendern = useDarfAendern();
  const wechseln = useStatusWechsel();
  const { werte, setze, zuruecksetzen, aktiv, suche } = useFilter();
  const [sort, setSort] = useState(anfang.sort);
  const [spalten, setSpalten] = useState<string[]>(() => spaltenWahl(anfang.spalten));
  const [breiten, setBreiten] = useState<Record<string, number>>(anfang.breiten);
  const [fokus, setFokus] = useState(0);
  const [sucheText, setSucheText] = useState(werte.q);
  const [dialog, setDialog] = useState<{ opp: OpportunityZeile | null } | null>(null);

  // Suche verzögert in die Adresse schreiben (wie in den Lead-Listen)
  useEffect(() => {
    const t = window.setTimeout(() => {
      if (sucheText.trim() !== werte.q) setze({ q: sucheText });
    }, 250);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sucheText]);
  useEffect(() => setSucheText(werte.q), [werte.q]);

  const filter = useMemo(
    () => ({
      pipeline_id: werte.pipeline ?? undefined,
      user_id: werte.nutzer ?? undefined,
      smart_view_id: werte.view ?? undefined,
      feld: werte.feld,
      von: werte.zeitraum.von ?? undefined,
      bis: werte.zeitraum.bis ?? undefined,
      status_id: werte.status.length ? werte.status : undefined,
      q: werte.q || undefined,
      sort: sort as OpportunitySortierung,
    }),
    [werte.pipeline, werte.nutzer, werte.view, werte.feld, werte.zeitraum.von, werte.zeitraum.bis, werte.status, werte.q, sort],
  );
  const { zeilen, total, summe, cursor, laedt, laedtMehr, fehler, mehr, neuLaden, still, ersetzen } = useListe(filter);

  // anderer Filter: Fokus zurück an den Anfang
  useEffect(() => setFokus(0), [filter]);

  const neu = useCallback(() => setDialog({ opp: null }), []);
  useSeitenBefehle([{ id: "opp-neu", label: "Neue Opportunity", icon: "plus", gruppe: "Opportunities", aktion: neu }]);

  const sortieren = (s: { key: string; ab: boolean } | null) => {
    setSort(sortAlsApi(s, STANDARD_SORT));
    void ansichtMerken(LISTE, { sortierung: s ? [s] : null });
  };
  const spaltenWaehlen = (keys: string[] | null) => {
    setSpalten(spaltenWahl(keys));
    void ansichtMerken(LISTE, { spalten: keys });
  };

  const breiteSetzen = (key: string, px: number | null) => {
    const neu = { ...breiten };
    if (px === null) delete neu[key];
    else neu[key] = px;
    setBreiten(neu);
    void ansichtMerken(LISTE, { einstellungen: { breiten: Object.keys(neu).length ? neu : null } });
  };
  const spalteVerschoben = (von: string, nach: string) => {
    const neu = spalteVerschieben(spalten, von, nach, new Set(OPP_SPALTEN.filter((s) => s.fest).map((s) => s.key)));
    if (neu !== spalten) spaltenWaehlen(neu);
  };

  const statusVon = (o: OpportunityZeile) => (meta?.pipelines ?? []).find((p) => p.id === o.pipeline_id)?.statuses ?? [];
  const oeffnen = (o: OpportunityZeile) => navigate(`/lead/${encodeURIComponent(o.lead_id)}`);
  const statusSetzen = (o: OpportunityZeile, statusId: string) => void wechseln(o, statusId, { fertig: ersetzen, rollback: () => void still() });

  const wert = summe ? summe.aktiv + summe.gewonnen + summe.verloren : 0;

  return (
    <div className="liste-seite">
      <OpportunityKopf titel={T.nav.opportunities} suche={suche} onNeu={neu} />
      <Bedienleiste
        liste={LISTE}
        einheit={{ eins: "Opportunity", viele: "Opportunities" }}
        trefferZahl={total}
        geladen={zeilen.length}
        laedt={laedt}
        spalten={ALLE_SPALTEN}
        gezeigt={spalten}
        onSpalten={spaltenWaehlen}
        sort={apiAlsSort(sort)}
        sortFelder={SORT_FELDER}
        onSort={sortieren}
        suche={{ wert: sucheText, onAendern: setSucheText, platzhalter: "Lead, Kontakt oder Notiz …" }}
        vorn={
          <FilterLeiste
            werte={werte}
            pipelines={meta?.pipelines ?? []}
            pipelineId={werte.pipeline}
            onAendern={setze}
            onZuruecksetzen={zuruecksetzen}
            aktiv={aktiv}
            mitStatus
          />
        }
      />
      {fehler ? <FehlerBox fehler={fehler} onNochmal={() => void neuLaden()} /> : null}
      {laedt && !zeilen.length ? (
        <Laden />
      ) : !fehler && !zeilen.length ? (
        <Leer titel="Keine Opportunities gefunden." text={aktiv || werte.pipeline ? "Filter anpassen oder zurücksetzen." : "Es gibt noch keine Opportunities. Neue entstehen über „Opportunity“ oben rechts oder am Lead."}>
          {aktiv ? (
            <button type="button" className="knopf knopf--klein" onClick={zuruecksetzen}>
              Filter zurücksetzen
            </button>
          ) : null}
        </Leer>
      ) : zeilen.length ? (
        <OpportunityTabelle
          zeilen={zeilen}
          spalten={spalten}
          breiten={breiten}
          onBreite={breiteSetzen}
          onVerschieben={spalteVerschoben}
          sort={sort}
          onSort={(api) => sortieren(apiAlsSort(api))}
          fokus={Math.min(fokus, zeilen.length - 1)}
          setFokus={setFokus}
          onOeffnen={oeffnen}
          onBearbeiten={(o) => setDialog({ opp: o })}
          onStatus={statusSetzen}
          darfAendern={darfAendern}
          statusVon={statusVon}
          onEnde={cursor ? () => void mehr() : undefined}
          fuss={
            cursor ? (
              <div className="tabelle__mehr">
                <button type="button" className="knopf" disabled={laedtMehr} onClick={() => void mehr()}>
                  {laedtMehr ? "Lädt …" : "Weitere laden"}
                </button>
              </div>
            ) : null
          }
        />
      ) : null}
      {summe && wert > 0 ? (
        <div className="opp-summe" title="Jahreswert: monatliche Beträge mal 12">
          <span>
            Aktiv <b>{euro(summe.aktiv)}</b>
          </span>
          <span>
            Gewonnen <b>{euro(summe.gewonnen)}</b>
          </span>
          <span>
            Verloren <b>{euro(summe.verloren)}</b>
          </span>
        </div>
      ) : null}
      <OpportunityDialog
        offen={dialog !== null}
        onSchliessen={() => setDialog(null)}
        opportunity={dialog?.opp ?? null}
        onGespeichert={(opp) => {
          ersetzen(opp);
          void still();
        }}
      />
    </div>
  );
}
